// Central SEO / GEO / LLM configuration and structured-data builders.
// This is the single source of truth for the public domain and brand identity,
// so the live custom domain (www.yawctv.com) only needs to change in one place.

export const SITE = {
  name: 'Yaw CTV',
  legalName: 'Yaw Media Group',
  url: 'https://www.yawctv.com',
  domain: 'www.yawctv.com',
  description:
    'SDVOSB & MBO certified digital marketing agency for government and business advertising — media buying, OTT/CTV, programmatic, geo-targeted, and social campaigns for federal, state, and local agencies.',
  contactEmail: 'ads@yawctv.com',
  founded: '2018',
  locality: 'Orlando',
  region: 'FL',
  country: 'US',
  logo: 'https://www.yawctv.com/logo.png',
  ogImage: 'https://www.yawctv.com/og-image.png',
  sameAs: ['https://www.linkedin.com/company/yawdigital'],
} as const;

export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;

type Json = Record<string, unknown>;

export interface SeoConfig {
  title: string;
  description?: string;
  /** Path relative to the site root, e.g. "/contact". Defaults to "/". */
  path?: string;
  image?: string;
  noindex?: boolean;
  /** og:type. Defaults to "website". */
  type?: string;
  /** Route-specific JSON-LD injected into <head> and cleaned up on unmount. */
  jsonLd?: Json | Json[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const ROUTE_LD_ID = 'route-jsonld';

/** Apply per-route metadata to <head>. Safe to call on every route change. */
export function applySeo(cfg: SeoConfig) {
  const url = `${SITE.url}${cfg.path ?? '/'}`;
  const description = cfg.description ?? SITE.description;
  const image = cfg.image ?? SITE.ogImage;
  const robots = cfg.noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = cfg.title;
  upsertMeta('name', 'title', cfg.title);
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', robots);
  upsertLink('canonical', url);

  // Open Graph
  upsertMeta('property', 'og:title', cfg.title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:type', cfg.type ?? 'website');
  upsertMeta('property', 'og:site_name', SITE.name);

  // Twitter
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', cfg.title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:url', url);
  upsertMeta('name', 'twitter:image', image);

  // Route-specific JSON-LD (replaces any previous route graph)
  document.getElementById(ROUTE_LD_ID)?.remove();
  if (cfg.jsonLd) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = ROUTE_LD_ID;
    script.text = JSON.stringify(cfg.jsonLd);
    document.head.appendChild(script);
  }
}

export function clearRouteJsonLd() {
  document.getElementById(ROUTE_LD_ID)?.remove();
}

// ---------------------------------------------------------------------------
// JSON-LD builders (for runtime, route-specific injection)
// ---------------------------------------------------------------------------

export function breadcrumbLd(items: { name: string; path: string }[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path}`,
    })),
  };
}

export function contactPageLd(): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${SITE.url}/contact#webpage`,
    url: `${SITE.url}/contact`,
    name: 'Contact Yaw CTV',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
    inLanguage: 'en-US',
    description:
      'Request a free consultation for government and business advertising campaigns — media buying, OTT/CTV, programmatic, and social.',
  };
}

export function faqPageLd(faqs: { question: string; answer: string }[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}
